const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR
const WEEK = 7 * DAY
const MONTH = 30 * DAY
const YEAR = 365 * DAY

/**
 * Coarse relative-time label for a bill's `lastRefreshedAt` stamp, e.g. "3 weeks ago".
 * Returns null for a null/invalid input so callers can skip rendering the hint entirely.
 * `now` is injectable for deterministic tests; defaults to the real clock.
 */
export function formatSyncedAgo(iso: string | null, now: Date = new Date()): string | null {
  if (!iso) return null
  const then = new Date(iso)
  if (Number.isNaN(then.getTime())) return null

  const diffMs = now.getTime() - then.getTime()
  if (diffMs < MINUTE) return 'just now'

  const steps: Array<[number, string]> = [
    [YEAR, 'year'],
    [MONTH, 'month'],
    [WEEK, 'week'],
    [DAY, 'day'],
    [HOUR, 'hour'],
  ]
  for (const [unitMs, label] of steps) {
    if (diffMs >= unitMs) {
      const n = Math.floor(diffMs / unitMs)
      return `${n} ${label}${n === 1 ? '' : 's'} ago`
    }
  }
  // Fell through every larger unit: the `diffMs < MINUTE` guard above means we're always
  // here with MINUTE <= diffMs < HOUR, so minutes is the guaranteed remaining case.
  const n = Math.floor(diffMs / MINUTE)
  return `${n} minute${n === 1 ? '' : 's'} ago`
}

/** Most recent `lastRefreshedAt` among a template's linked bills, or null if none are set. */
export function mostRecentRefresh(stamps: Array<string | null>): string | null {
  const known = stamps.filter((s): s is string => !!s)
  if (known.length === 0) return null
  return known.reduce((latest, s) => (s > latest ? s : latest))
}
